import { UploadOutlined } from "@ant-design/icons";
import { Modal, Upload } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { useUpdateTableDataMutation } from "components";
import type { ImportResult } from "components/FeatureButtons/types";
import i18n from "i18next";
import type { FC } from "react";
import { useState } from "react";

import { PrimaryButton, SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { RenderImportResult, uploadPropsUtils } from "../../../../../components/FeatureButtons/csvModalButtonUtils";
import { ProgressBar } from "../../../../../components/ProgressBar/ProgressBar";

export type ZipImportButtonProps = {
    translationKey: string;
    isImport: boolean;
    callback?: (file: File) => void;
    onSuccess?: () => void;
    urlString: string;
};

export const ZipImportButton: FC<ZipImportButtonProps> = ({ translationKey, isImport, callback, onSuccess, urlString }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [showAllErrors, setShowAllErrors] = useState(false);

    const showModal = () => {
        setIsModalVisible(true);
        setImportResult(null);
    };

    const handleClose = () => {
        setIsModalVisible(false);
        setFileList([]);
        setImportResult(null);
        setShowAllErrors(false);
    };

    const [importDataRequest] = useUpdateTableDataMutation();

    const handleImport = async () => {
        if (fileList.length === 0) {
            return;
        }

        const file = fileList[0].originFileObj as File;

        if (callback) {
            callback(file);
            handleClose();
            return;
        }

        setUploading(true);
        setImportResult(null);

        const formData = new FormData();
        formData.append("importFile", file);
        formData.append("format", "zip");

        importDataRequest({
            url: urlString,
            method: isImport ? "POST" : "PUT",
            body: formData,
        })
            .unwrap()
            .then((result) => {
                // @ts-ignore
                setImportResult(result);
                if (onSuccess) {
                    onSuccess();
                }
            })
            .catch((error) => {
                setImportResult({
                    importErrors: [error?.data?.message || "batchUpdateData.modal.importError"],
                });
            })
            .finally(() => setUploading(false));
    };

    const uploadProps = uploadPropsUtils(fileList, setFileList);

    const hasBeenSuccessfulImport = !!importResult && (!importResult.importErrors || importResult.importErrors.length === 0);

    return (
        <>
            <SecondaryButton onClick={showModal} icon={<UploadOutlined />}>
                {i18n.t(translationKey)}
            </SecondaryButton>

            <Modal
                title={isImport ? i18n.t("zipImport.modal.title") : i18n.t("zipUpdate.modal.title")}
                open={isModalVisible}
                onCancel={handleClose}
                footer={[
                    <SecondaryButton key="close" onClick={handleClose}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    <PrimaryButton
                        key="import"
                        onClick={handleImport}
                        disabled={fileList.length === 0 || uploading || hasBeenSuccessfulImport}
                    >
                        {i18n.t("button.import")}
                    </PrimaryButton>,
                ]}
            >
                <p>{isImport ? i18n.t("zipImport.modal.description") : i18n.t("zipUpdate.modal.description")}</p>
                <Upload {...uploadProps}>
                    <SecondaryButton icon={<UploadOutlined />}>{i18n.t("button.chooseFile")}</SecondaryButton>
                </Upload>
                {uploading && <ProgressBar />}
                {importResult && (
                    <RenderImportResult
                        importResult={importResult}
                        showAllErrors={showAllErrors}
                        setShowAllErrors={setShowAllErrors}
                        errorString={"zipImport.error"}
                        successString={"zipImport.success"}
                    />
                )}
            </Modal>
        </>
    );
};
